/** **AutoLISP's EVALUATOR — the subset a drafter's macros and this project's benchmarks use.**
 *
 * ⚠⚠⚠ **WHY THIS EXISTS AT ALL, and it is two reasons rather than one.** The obvious one is the
 * feature: every AutoCAD office has `.lsp` files, and an app that cannot load them is not a
 * replacement. The second is measurement. `qa/reference/pddrawloop.lsp` drives BricsCAD through
 * `(command "_.LINE" p1 p2 "")` — no keyboard, no prompt, no typing — and pdcad has only ever
 * been driven by xdotool. So every comparison this project has published put a program that was
 * TYPED AT beside one that was CALLED, and the difference (30-50 ms a command, measured) was
 * inside pdcad's column. Running the SAME FILE against both is the only apple-to-apple there is.
 *
 * ⚠⚠ **IT LIVES IN THE STUDIO, NOT IN THE ENGINE, AND THAT IS AutoCAD'S OWN SHAPE.** AutoLISP is
 * an EDITOR extension language: it drives commands, reads sysvars and asks the user questions.
 * `pdcad-core` knows none of those things — it knows entities. An interpreter in Rust would have
 * to call back out to the studio for `(command …)` anyway, and `(command …)` is asynchronous,
 * which is exactly what a synchronous wasm call cannot be.
 *
 * ⚠ Everything is `async`. A caller cannot know which functions reach `(command …)`, so they all
 * have to be awaitable — including `+`.
 */
import { readAll, Sym, type Fn, type Lv } from "./read";

/** What the interpreter needs from the app around it. ⚠ An INTERFACE, so `qa/` can evaluate LISP
 *  against a stub with no drawing at all — which is how the arithmetic is tested. */
export interface LispHost {
  /** Run one command to completion. ⚠ `""` is an empty Enter — `(command "_.LINE" p1 p2 "")`. */
  command: (name: string, args: string[]) => Promise<void>;
  getvar: (name: string) => Lv;
  setvar: (name: string, value: Lv) => void;
  /** A line at the command line. */
  say: (line: string) => void;
  /** ⚠⚠ A browser cannot write `/tmp`. `close` hands the text here instead, and the harness
   *  reads it off `window.__pdcadLispFiles`; the desktop app can write a real file later. A
   *  script that "wrote its result" and silently wrote nothing would be the worst of both. */
  file: (path: string, text: string) => void;
}

class Env {
  constructor(readonly vars: Map<string, Lv>, readonly up: Env | null) {}
  get(n: string): Lv {
    for (let e: Env | null = this; e; e = e.up) if (e.vars.has(n)) return e.vars.get(n)!;
    return null;                       // ⚠ An unset symbol is `nil`, not an error — AutoLISP's own
  }
  set(n: string, v: Lv) {
    for (let e: Env | null = this; e; e = e.up) if (e.vars.has(n)) { e.vars.set(n, v); return; }
    // ⚠⚠ **AN UNDECLARED NAME IS GLOBAL, NOT LOCAL TO WHATEVER BLOCK SET IT.** AutoLISP has no
    // block scope: `(foreach x lst (setq total (+ total x)))` accumulates into a global, and a
    // `setq` that landed in the foreach's own frame answered `nil` the moment the loop ended —
    // caught by `qa/lisp_check.py` on exactly that form. Locals exist only where a `defun`
    // declares them after the slash.
    let root: Env = this;
    while (root.up) root = root.up;
    root.vars.set(n, v);
  }
  /** ⚠ Declared HERE, shadowing whatever is above — `(defun f ( / x) …)`'s locals. */
  local(n: string) { this.vars.set(n, null); }
}

const isSym = (v: Lv): v is Sym => v instanceof Sym;
const isFn = (v: Lv): v is Fn => !!v && typeof v === "object" && (v as Fn).lisp === true;
// ⚠⚠ `undefined` too: a builtin called with fewer arguments than it reads gets `undefined` from
// JavaScript and `nil` is what AutoLISP would have handed it. `(strcase "aB")` lower-cased its
// argument because the absent second one tested as true.
const truthy = (v: Lv | undefined) =>
  v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0);

const num = (v: Lv): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string") { const n = Number(v); return Number.isFinite(n) ? n : 0; }
  return 0;
};

/** ⚠⚠ **HOW A VALUE REACHES A COMMAND, and it is the one place the two languages meet.** A point
 *  is a LIST in LISP and `x,y` at a prompt; a number is `150`; a string is itself. Getting this
 *  wrong is silent — the command takes the text and draws something else. */
export function asAnswer(v: Lv): string {
  if (v === null) return "";
  if (Array.isArray(v)) return v.map((x) => fmt(num(x))).join(",");
  if (typeof v === "number") return fmt(v);
  if (isSym(v)) return v.name;
  return String(v);
}

/** ⚠ 12 significant digits and no exponent: a command line reading `1e+21` is a command line
 *  reading a word. AutoCAD's own `rtos` default is 4 decimals, which is NOT enough for a
 *  coordinate in millimetres on an 80 m plan. */
const fmt = (n: number) => {
  if (!Number.isFinite(n)) return "0";
  const s = n.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  return s === "-0" ? "0" : s;
};

export function lispPrint(v: Lv): string {
  if (v === null) return "nil";
  if (Array.isArray(v)) return `(${v.map(lispPrint).join(" ")})`;
  if (isSym(v)) return v.name;
  if (isFn(v)) return `#<subr ${v.name}>`;
  if (typeof v === "number") return fmt(v);
  return String(v);
}

export class Lisp {
  readonly global = new Env(new Map(), null);
  /** `(defun c:FOO …)` — the commands a loaded file adds to the app. */
  readonly commands = new Map<string, Fn>();
  private files = new Map<number, { path: string; lines: string[] }>();
  private nextFile = 1;

  constructor(private readonly host: LispHost) {
    this.global.vars.set("T", new Sym("T"));
    this.global.vars.set("PI", Math.PI);
    for (const [name, call] of this.builtins()) {
      this.global.vars.set(name, { lisp: true, name, call } as Fn);
    }
  }

  /** Evaluate every form in `src`, in order, and answer the LAST one — `(load …)`'s contract. */
  async run(src: string): Promise<Lv> {
    let last: Lv = null;
    for (const f of readAll(src)) last = await this.eval(f, this.global);
    return last;
  }

  async eval(form: Lv, env: Env): Promise<Lv> {
    if (form === null || typeof form === "number" || typeof form === "string") return form;
    if (isSym(form)) return env.get(form.name);
    if (!Array.isArray(form)) return form;
    if (!form.length) return null;

    const head = form[0];
    if (isSym(head)) {
      const s = await this.special(head.name, form, env);
      if (s !== NOT_SPECIAL) return s;
    }
    const fn = await this.eval(head, env);
    if (!isFn(fn)) {
      throw new Error(`no function definition: ${isSym(head) ? head.name : lispPrint(head)}`);
    }
    const args: Lv[] = [];
    for (let i = 1; i < form.length; i++) args.push(await this.eval(form[i], env));
    return fn.call(args);
  }

  /** The special forms — the ones whose arguments are NOT all evaluated first. */
  private async special(name: string, form: Lv[], env: Env): Promise<Lv | typeof NOT_SPECIAL> {
    switch (name) {
      case "QUOTE": return form[1] ?? null;
      case "SETQ": {
        // ⚠ Pairs: `(setq a 1 b 2)`. A single `(setq a)` sets nil, which is AutoLISP's answer.
        let v: Lv = null;
        for (let i = 1; i < form.length; i += 2) {
          const s = form[i];
          if (!isSym(s)) throw new Error("setq wants a symbol");
          v = i + 1 < form.length ? await this.eval(form[i + 1], env) : null;
          env.set(s.name, v);
        }
        return v;
      }
      case "DEFUN": {
        const nameSym = form[1];
        if (!isSym(nameSym)) throw new Error("defun wants a name");
        const spec = (form[2] as Lv[]) || [];
        // ⚠⚠ `( a b / x y )` — arguments before the slash, LOCALS after it. A file that declares
        // its locals and gets globals instead works until two of them share a name.
        const params: string[] = [];
        const locals: string[] = [];
        let seenSlash = false;
        for (const p of spec) {
          if (isSym(p) && p.name === "/") { seenSlash = true; continue; }
          if (isSym(p)) (seenSlash ? locals : params).push(p.name);
        }
        const body = form.slice(3);
        const fn: Fn = {
          lisp: true,
          name: nameSym.name,
          call: async (args) => {
            const inner = new Env(new Map(), this.global);
            params.forEach((p, i) => inner.vars.set(p, args[i] ?? null));
            locals.forEach((l) => inner.local(l));
            let out: Lv = null;
            for (const b of body) out = await this.eval(b, inner);
            return out;
          },
        };
        this.global.vars.set(nameSym.name, fn);
        // ⚠⚠ **`c:` MAKES IT A COMMAND**, which is the whole point of a drafter's `.lsp` file.
        if (nameSym.name.startsWith("C:")) this.commands.set(nameSym.name.slice(2), fn);
        return nameSym;
      }
      case "IF": {
        if (truthy(await this.eval(form[1], env))) return this.eval(form[2] ?? null, env);
        return form.length > 3 ? this.eval(form[3], env) : null;
      }
      case "COND": {
        for (let i = 1; i < form.length; i++) {
          const clause = form[i] as Lv[];
          if (!Array.isArray(clause) || !clause.length) continue;
          const test = await this.eval(clause[0], env);
          if (!truthy(test)) continue;
          let out: Lv = test;
          for (let j = 1; j < clause.length; j++) out = await this.eval(clause[j], env);
          return out;
        }
        return null;
      }
      case "WHILE": {
        let out: Lv = null;
        // ⚠ A bound, and it is not paranoia: a `while` whose test never changes freezes the tab,
        // and a drafter's macro is exactly where that typo lives. 2,000,000 iterations is far
        // more than any real loop and returns in seconds rather than never.
        for (let n = 0; truthy(await this.eval(form[1], env)); n++) {
          if (n > 2_000_000) throw new Error("while: too many iterations — is the test changing?");
          for (let i = 2; i < form.length; i++) out = await this.eval(form[i], env);
        }
        return out;
      }
      case "REPEAT": {
        const n = num(await this.eval(form[1], env));
        let out: Lv = null;
        for (let k = 0; k < n; k++) {
          for (let i = 2; i < form.length; i++) out = await this.eval(form[i], env);
        }
        return out;
      }
      case "PROGN": {
        let out: Lv = null;
        for (let i = 1; i < form.length; i++) out = await this.eval(form[i], env);
        return out;
      }
      case "FOREACH": {
        const s = form[1];
        if (!isSym(s)) throw new Error("foreach wants a symbol");
        const lst = await this.eval(form[2], env);
        let out: Lv = null;
        for (const item of Array.isArray(lst) ? lst : []) {
          const inner = new Env(new Map([[s.name, item]]), env);
          for (let i = 3; i < form.length; i++) out = await this.eval(form[i], inner);
        }
        return out;
      }
      case "AND": {
        let out: Lv = new Sym("T");
        for (let i = 1; i < form.length; i++) {
          out = await this.eval(form[i], env);
          if (!truthy(out)) return null;
        }
        return out;
      }
      case "OR": {
        for (let i = 1; i < form.length; i++) {
          const v = await this.eval(form[i], env);
          if (truthy(v)) return v;
        }
        return null;
      }
      case "LAMBDA": {
        const spec = (form[1] as Lv[]) || [];
        const params = spec.filter(isSym).map((p) => p.name);
        const body = form.slice(2);
        return {
          lisp: true, name: "lambda",
          call: async (args) => {
            const inner = new Env(new Map(), env);
            params.forEach((p, i) => inner.vars.set(p, args[i] ?? null));
            let out: Lv = null;
            for (const b of body) out = await this.eval(b, inner);
            return out;
          },
        } as Fn;
      }
      default: return NOT_SPECIAL;
    }
  }

  /** A value used where a FUNCTION is wanted: a quoted `(lambda …)` list becomes one. */
  private async asFn(v: Lv): Promise<Lv> {
    if (isFn(v)) return v;
    if (Array.isArray(v) && v.length && isSym(v[0]) && v[0].name === "LAMBDA") {
      return this.eval(v, this.global);
    }
    return null;
  }

  private builtins(): [string, (a: Lv[]) => Promise<Lv>][] {
    const h = this.host;
    const n = num;
    const B = (f: (a: Lv[]) => Lv | Promise<Lv>) => async (a: Lv[]) => f(a);
    return [
      // ── arithmetic ────────────────────────────────────────────────────────────────────────
      ["+", B((a) => a.reduce((s: number, x) => s + n(x), 0))],
      ["-", B((a) => (a.length === 1 ? -n(a[0]) : a.slice(1).reduce((s: number, x) => s - n(x), n(a[0]))))],
      ["*", B((a) => a.reduce((s: number, x) => s * n(x), 1))],
      // ⚠ Division by zero is an ERROR in AutoLISP, not Infinity. A wall at Infinity metres is
      // not a drawing anybody can recover.
      ["/", B((a) => a.slice(1).reduce((s: number, x) => {
        if (n(x) === 0) throw new Error("divide by zero");
        return s / n(x);
      }, n(a[0])))],
      ["1+", B((a) => n(a[0]) + 1)],
      ["1-", B((a) => n(a[0]) - 1)],
      ["ABS", B((a) => Math.abs(n(a[0])))],
      ["MIN", B((a) => Math.min(...a.map(n)))],
      ["MAX", B((a) => Math.max(...a.map(n)))],
      ["SQRT", B((a) => Math.sqrt(n(a[0])))],
      ["EXPT", B((a) => n(a[0]) ** n(a[1]))],
      ["SIN", B((a) => Math.sin(n(a[0])))],
      ["COS", B((a) => Math.cos(n(a[0])))],
      ["ATAN", B((a) => (a.length > 1 ? Math.atan2(n(a[0]), n(a[1])) : Math.atan(n(a[0]))))],
      ["FIX", B((a) => Math.trunc(n(a[0])))],
      ["REM", B((a) => n(a[0]) % n(a[1]))],
      // ── comparison ────────────────────────────────────────────────────────────────────────
      // ⚠⚠ `=` compares NUMBERS and STRINGS both, because `(= k "LINE")` is how every driver
      // script branches. `eq` is identity and `equal` is deep; conflating the three is how a
      // list comparison starts answering nil.
      ["=", B((a) => (cmpEq(a[0], a[1]) ? new Sym("T") : null))],
      ["/=", B((a) => (cmpEq(a[0], a[1]) ? null : new Sym("T")))],
      ["<", B((a) => (ord(a) < 0 ? new Sym("T") : null))],
      [">", B((a) => (ord(a) > 0 ? new Sym("T") : null))],
      ["<=", B((a) => (ord(a) <= 0 ? new Sym("T") : null))],
      [">=", B((a) => (ord(a) >= 0 ? new Sym("T") : null))],
      ["EQ", B((a) => (a[0] === a[1] || cmpEq(a[0], a[1]) ? new Sym("T") : null))],
      ["EQUAL", B((a) => (lispPrint(a[0]) === lispPrint(a[1]) ? new Sym("T") : null))],
      ["NULL", B((a) => (a[0] === null ? new Sym("T") : null))],
      ["NOT", B((a) => (truthy(a[0]) ? null : new Sym("T")))],
      ["ZEROP", B((a) => (n(a[0]) === 0 ? new Sym("T") : null))],
      // ── lists ─────────────────────────────────────────────────────────────────────────────
      ["LIST", B((a) => a.slice())],
      ["CAR", B((a) => (Array.isArray(a[0]) && a[0].length ? a[0][0] : null))],
      ["CDR", B((a) => (Array.isArray(a[0]) && a[0].length > 1 ? a[0].slice(1) : null))],
      ["CADR", B((a) => (Array.isArray(a[0]) && a[0].length > 1 ? a[0][1] : null))],
      ["CADDR", B((a) => (Array.isArray(a[0]) && a[0].length > 2 ? a[0][2] : null))],
      ["CONS", B((a) => [a[0], ...(Array.isArray(a[1]) ? a[1] : a[1] === null ? [] : [a[1]])])],
      ["NTH", B((a) => (Array.isArray(a[1]) ? a[1][n(a[0])] ?? null : null))],
      ["LENGTH", B((a) => (Array.isArray(a[0]) ? a[0].length : 0))],
      ["APPEND", B((a) => a.flatMap((x) => (Array.isArray(x) ? x : x === null ? [] : [x])))],
      ["REVERSE", B((a) => (Array.isArray(a[0]) ? a[0].slice().reverse() : a[0]))],
      ["LAST", B((a) => (Array.isArray(a[0]) && a[0].length ? a[0][a[0].length - 1] : null))],
      ["MEMBER", B((a) => {
        const l = Array.isArray(a[1]) ? a[1] : [];
        const at = l.findIndex((x) => cmpEq(x, a[0]));
        return at < 0 ? null : l.slice(at);
      })],
      ["MAPCAR", async (a) => {
        // ⚠⚠ **`'(lambda (x) …)` IS A QUOTED LIST, and it is how every manual writes this.**
        // `mapcar` is handed the list itself rather than a function, and AutoLISP applies it
        // anyway. Refusing it would make the commonest idiom in the language answer nil.
        const fn = isFn(a[0]) ? a[0] : await this.asFn(a[0]);
        if (!isFn(fn)) return null;
        const l = Array.isArray(a[1]) ? a[1] : [];
        const out: Lv[] = [];
        for (const x of l) out.push(await fn.call([x]));
        return out;
      }],
      // ── geometry a drafter's macro cannot do without ──────────────────────────────────────
      // ⚠ Angles are RADIANS in AutoLISP and degrees at the command line. A macro that mixes
      // them draws something plausible and wrong, so both are here and neither converts.
      ["POLAR", B((a) => {
        const p = Array.isArray(a[0]) ? a[0] : [0, 0];
        const ang = n(a[1]), d = n(a[2]);
        return [n(p[0]) + d * Math.cos(ang), n(p[1]) + d * Math.sin(ang)];
      })],
      ["DISTANCE", B((a) => {
        const p = Array.isArray(a[0]) ? a[0] : [0, 0], q = Array.isArray(a[1]) ? a[1] : [0, 0];
        return Math.hypot(n(q[0]) - n(p[0]), n(q[1]) - n(p[1]));
      })],
      ["ANGLE", B((a) => {
        const p = Array.isArray(a[0]) ? a[0] : [0, 0], q = Array.isArray(a[1]) ? a[1] : [0, 0];
        const t = Math.atan2(n(q[1]) - n(p[1]), n(q[0]) - n(p[0]));
        return t < 0 ? t + 2 * Math.PI : t;
      })],
      // ── strings and conversion ────────────────────────────────────────────────────────────
      ["STRCAT", B((a) => a.map((x) => (x === null ? "" : String(typeof x === "number" ? fmt(x) : x))).join(""))],
      ["STRLEN", B((a) => String(a[0] ?? "").length)],
      ["SUBSTR", B((a) => {
        const s = String(a[0] ?? ""), at = n(a[1]);
        // ⚠ AutoLISP counts from 1.
        return a.length > 2 ? s.substr(at - 1, n(a[2])) : s.substr(at - 1);
      })],
      ["STRCASE", B((a) => (truthy(a[1]) ? String(a[0] ?? "").toLowerCase()
                                         : String(a[0] ?? "").toUpperCase()))],
      ["ATOI", B((a) => parseInt(String(a[0] ?? "0"), 10) || 0)],
      ["ATOF", B((a) => parseFloat(String(a[0] ?? "0")) || 0)],
      ["ITOA", B((a) => String(Math.trunc(n(a[0]))))],
      // ⚠ `(rtos v mode prec)` — mode 2 is decimal, which is what every script here asks for.
      ["RTOS", B((a) => n(a[0]).toFixed(a.length > 2 ? n(a[2]) : 4))],
      ["TYPE", B((a) => {
        const v = a[0];
        if (v === null) return null;
        if (typeof v === "number") return new Sym(Number.isInteger(v) ? "INT" : "REAL");
        if (typeof v === "string") return new Sym("STR");
        if (Array.isArray(v)) return new Sym("LIST");
        if (isSym(v)) return new Sym("SYM");
        return new Sym("SUBR");
      })],
      // ── the app ───────────────────────────────────────────────────────────────────────────
      // ⚠⚠⚠ **`(command …)` — the reason this interpreter exists.** Every argument becomes an
      // ANSWER at a prompt: a list is a point, a number is a number, `""` is an empty Enter.
      // The studio's queue takes it from there, so a LISP-driven command and a typed one are
      // the same command.
      ["COMMAND", async (a) => {
        if (!a.length) return null;
        const name = String(a[0] ?? "").replace(/^[_.]+/, "").replace(/^\./, "");
        await h.command(name.toUpperCase(), a.slice(1).map(asAnswer));
        return null;
      }],
      ["GETVAR", B((a) => h.getvar(String(a[0] ?? "").toUpperCase()))],
      ["SETVAR", B((a) => { h.setvar(String(a[0] ?? "").toUpperCase(), a[1] ?? null); return a[1] ?? null; })],
      ["PRINC", B((a) => { if (a.length) h.say(`  ${typeof a[0] === "string" ? a[0] : lispPrint(a[0])}`); return a[0] ?? null; })],
      ["PRINT", B((a) => { h.say(`  ${lispPrint(a[0] ?? null)}`); return a[0] ?? null; })],
      ["PROMPT", B((a) => { h.say(`  ${String(a[0] ?? "")}`); return null; })],
      ["TERPRI", B(() => null)],
      // ── files, as far as a browser can honour them ────────────────────────────────────────
      ["OPEN", B((a) => {
        const id = this.nextFile++;
        this.files.set(id, { path: String(a[0] ?? ""), lines: [] });
        return id;
      })],
      ["WRITE-LINE", B((a) => {
        const f = this.files.get(n(a[1]));
        if (f) f.lines.push(String(a[0] ?? ""));
        return a[0] ?? null;
      })],
      ["CLOSE", B((a) => {
        const f = this.files.get(n(a[0]));
        if (f) { h.file(f.path, f.lines.join("\n")); this.files.delete(n(a[0])); }
        return null;
      })],
    ];
  }
}

const NOT_SPECIAL = Symbol("not-special");

const cmpEq = (a: Lv, b: Lv) => {
  if (typeof a === "number" || typeof b === "number") return num(a) === num(b);
  if (a === null || b === null) return a === b;
  return lispPrint(a) === lispPrint(b);
};

const ord = (a: Lv[]) => {
  // ⚠ Strings compare as STRINGS — `(< "a" "b")` is true in AutoLISP and has to stay true here.
  if (typeof a[0] === "string" && typeof a[1] === "string") {
    return a[0] < a[1] ? -1 : a[0] > a[1] ? 1 : 0;
  }
  const x = num(a[0]), y = num(a[1]);
  return x < y ? -1 : x > y ? 1 : 0;
};
